// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

interface IRequestParams {
  url: string;
  body?: object;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  queryParams?: Record<string, any>;
  headers?: Record<string, string>;
}

// Longest body we will quote into an error. Proxy failures return HTML error
// pages, and dumping a whole page into every Faro exception is not useful.
const maxDetailLength = 200;

const truncate = (detail: string) => {
  const collapsed = detail.replace(/\s+/g, ' ').trim();
  return collapsed.length > maxDetailLength ? `${collapsed.slice(0, maxDetailLength)}...` : collapsed;
};

// Error bodies across our API routes use three different keys, so check all of
// them rather than only the one products happens to send.
const errorBodyKeys = ['message', 'error', 'response'];

// Pick the most specific detail available. A body key beats statusText, which
// for a 500 is just the generic reason phrase and adds nothing to the status
// code. statusText is also absent over HTTP/2, so it cannot be relied on.
const getErrorDetail = (statusText: string, bodyText: string) => {
  let parsedBody;

  try {
    parsedBody = JSON.parse(bodyText);
  } catch {
    // Not JSON. A proxy error page or plain text is still better than nothing.
    return truncate(bodyText.length > 0 ? bodyText : statusText);
  }

  // A bare JSON string body is itself the detail.
  if (typeof parsedBody === 'string') {
    return truncate(parsedBody.length > 0 ? parsedBody : statusText);
  }

  for (const key of errorBodyKeys) {
    const value = parsedBody?.[key];
    if (typeof value === 'string' && value.length > 0) {
      return truncate(value);
    }
  }

  // An unrecognised shape (nested keys, or a gRPC-style code/details pair) still
  // carries more than the status phrase does, so quote the body rather than
  // discarding it. An empty container such as '{}' or '[]' carries nothing.
  const bodyHasContent =
    parsedBody !== null && typeof parsedBody === 'object' && Object.keys(parsedBody).length > 0;

  return truncate(bodyHasContent ? bodyText : statusText);
};

const request = async <T>({
  url = '',
  method = 'GET',
  body,
  queryParams = {},
  headers = {
    'content-type': 'application/json',
  },
}: IRequestParams): Promise<T> => {
  const urlWithQueryParams = `${url}?${new URLSearchParams(queryParams).toString()}`;
  const response = await fetch(urlWithQueryParams, {
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers,
  });

  const responseText = await response.text();
  const responseUrl = response.url && response.url.length > 0 ? response.url : urlWithQueryParams;

  if (!response.ok) {
      // An empty detail means the server sent no body and no status phrase,
      // which is what a proxy or gateway failure looks like from here. Say so
      // rather than trailing off after the status code.
      const errorDetail = getErrorDetail(response.statusText ?? '', responseText ?? '');
      const detail = errorDetail.length > 0 ? errorDetail : 'no detail returned by the server';
      throw new Error(
        "Response not ok. Status code: " + String(response.status) + ". Message: '" + detail + "'. Url: " + responseUrl
      );
  }

  if (!!responseText) return JSON.parse(responseText);

  return undefined as unknown as T;
};

export default request;
